<template>
  <div class="custom-filter-list">
    <LISAPopover class="custom-filter-popover" placement="bottom-start">
      <template #trigger="{ triggerAttrs }">
        <LISAButton
          v-bind="triggerAttrs"
          class="custom-filter-add-button"
          size="lg"
          variant="neutral"
          outlined
          icon="fas fa-plus"
        >
          {{ t("global.add") }}
          <span v-if="completeRowCount > 0" class="custom-filter-count">{{
            completeRowCount
          }}</span>
        </LISAButton>
      </template>
      <template #default>
        <div v-for="row in rows" :key="row.id" class="custom-filter-row">
          <InputRow
            v-slot="{ field: inputField }"
            name="customAttributeField"
            :model-value="row.fieldId"
          >
            <LISASelect
              v-bind="inputField"
              v-model="row.fieldId"
              :placeholder="t('global.all')"
              :options="fieldOptions"
              class="custom-field-select"
              @update:model-value="setDefaultValue(row)"
            />
          </InputRow>

          <InputRow
            v-slot="{ field: inputField }"
            name="customAttributeValue"
            :model-value="row.value"
          >
            <LISAInput
              v-if="selectedField(row) == null"
              v-bind="inputField"
              v-model="row.value"
              class="custom-field-value"
            />
            <LISAInput
              v-else-if="
                [SQDCEventActionTypeFieldType.TEXT, SQDCEventActionTypeFieldType.TEXTAREA].includes(
                  selectedField(row)!.type,
                )
              "
              v-bind="inputField"
              v-model="row.value"
              class="custom-field-value"
            />
            <LISAInputNumber
              v-else-if="selectedField(row)?.type === SQDCEventActionTypeFieldType.NUMBER"
              v-bind="inputField"
              v-model="row.value"
              class="custom-field-value"
            />
            <LISACheckbox
              v-else-if="selectedField(row)?.type === SQDCEventActionTypeFieldType.BOOLEAN"
              v-bind="inputField"
              v-model="row.value"
            />
            <PrimeVueSelect
              v-else-if="
                selectedField(row) != null && isSQDCButtonModuleType(selectedField(row)?.type)
              "
              v-bind="inputField"
              v-model="row.value"
              class="custom-field-value"
              :options="getButtonModuleSelectOptions(selectedField(row)?.type)"
              option-label="label"
              option-value="value"
              show-clear
            />
            <PrimeVueSelect
              v-else-if="
                selectedField(row)?.type === SQDCEventActionTypeFieldType.ENUM &&
                selectedField(row)?.isArray === false
              "
              v-bind="inputField"
              v-model="row.value"
              class="custom-field-value"
              :options="getEnumOptions(selectedField(row)?.EnumValues, locale, translateDynamic)"
              option-label="label"
              option-value="value"
              show-clear
            />
            <MultiSelect
              v-else-if="selectedField(row)?.type === SQDCEventActionTypeFieldType.ENUM"
              v-bind="inputField"
              v-model="row.value"
              class="custom-field-value"
              :options="getEnumOptions(selectedField(row)?.EnumValues, locale, translateDynamic)"
              option-label="label"
              option-value="value"
              display="chip"
              filter
            />
            <DatePickerComponent
              v-else-if="selectedField(row)?.type === SQDCEventActionTypeFieldType.DATE"
              v-model="row.value"
              class="custom-field-value"
              :type="selectedField(row)?.dateGranularity ?? DatePickerType.DAY"
              :clearable="true"
            />
            <CostCenterSelect
              v-else-if="
                selectedField(row)?.type === SQDCEventActionTypeFieldType.RELATION &&
                selectedField(row)?.relation === SQDCEventTypeFieldRelation.COST_CENTER
              "
              v-model="row.value"
              class="custom-field-value"
              :selection-mode="selectedField(row)?.isArray ? 'multiple' : undefined"
              :variables="{ siteIds: siteId == null ? undefined : [siteId], hierarchyMinLevel: 0 }"
              :show-inline-create="false"
            />
            <TechnicalPlaceSelect
              v-else-if="
                selectedField(row)?.type === SQDCEventActionTypeFieldType.RELATION &&
                selectedField(row)?.relation === SQDCEventTypeFieldRelation.TECHNICAL_PLACE
              "
              v-model="row.value"
              class="custom-field-value"
              :selection-mode="selectedField(row)?.isArray ? 'multiple' : undefined"
              :variables="{ siteIds: siteId == null ? undefined : [siteId], hierarchyMinLevel: 0 }"
              :show-inline-create="false"
            />
            <SearchSelect
              v-else-if="
                selectedField(row)?.type === SQDCEventActionTypeFieldType.RELATION &&
                selectedField(row)?.relation === SQDCEventTypeFieldRelation.USER
              "
              v-model="row.value"
              class="custom-field-value"
              :multiple="true"
              :mode="SelectMode.USER"
              :filters="{ siteIds: siteId == null ? undefined : [siteId], hide: false }"
              :placeholder="t('components.shared.searchSelect.UserSelect.selectUser').toString()"
            />
            <SearchSelect
              v-else-if="
                selectedField(row)?.type === SQDCEventActionTypeFieldType.RELATION &&
                selectedField(row)?.relation === SQDCEventTypeFieldRelation.IDEA_CATEGORY
              "
              v-model="row.value"
              class="custom-field-value"
              :multiple="selectedField(row)?.isArray"
              :mode="SelectMode.IDEA_CATEGORY"
            />
            <SearchSelect
              v-else-if="selectedField(row)?.type === SQDCEventActionTypeFieldType.PERCENT_SHARING"
              v-model="row.value"
              class="custom-field-value"
              :multiple="true"
              :mode="SelectMode.USER"
              :filters="{ siteIds: siteId == null ? undefined : [siteId], hide: false }"
              :placeholder="t('components.shared.searchSelect.UserSelect.selectUser').toString()"
            />
            <div
              v-else-if="selectedField(row)?.type === SQDCEventActionTypeFieldType.OUTAGE"
              class="custom-outage-filter"
            >
              <LISACheckbox v-model="row.value.hasOutage" />
              <span>{{ t("global.yes") }}</span>
              <DatePickerComponent
                v-if="row.value.hasOutage"
                v-model="row.value.dateRange"
                class="custom-field-value"
                :type="DatePickerType.DAY"
                :range="true"
                :clearable="true"
              />
            </div>
            <div
              v-else
              class="lisaui:flex lisaui:items-center custom-field-value"
              style="height: 2.85em"
            >
              {{ t("global.na") }}
            </div>
          </InputRow>

          <LISAButton
            class="custom-filter-icon-button"
            size="md"
            variant="error"
            icon="fas fa-trash-alt"
            :aria-label="t('global.delete')"
            @click="removeRow(row.id)"
          />
        </div>
        <LISAButton
          class="custom-filter-add-button"
          size="lg"
          variant="neutral"
          outlined
          icon="fas fa-plus"
          @click="addRow()"
        >
          {{ t("global.add") }}
        </LISAButton>
      </template>
    </LISAPopover>
  </div>
</template>

<script setup lang="ts">
import InputRow from "@/components/shared/form/InputRow.vue";
import PrimeVueSelect from "primevue/select";
import MultiSelect from "primevue/multiselect";
import { computed, ref } from "vue";
import { DateTime } from "luxon";
import CustomAttributeFilter from "../filterImplementations/CustomAttributeFilter";
import {
  LISACheckbox,
  LISAButton,
  LISAInput,
  LISAPopover,
  LISASelect,
  type LISASelectOption,
} from "@fi-solutions/lisa-ui";
import { FilterUIProps } from "../FilterTypes";
import { useTranslation } from "@/language/translation";
import {
  DatePickerType,
  SQDCEventActionTypeFieldType,
  SQDCEventTypeFieldRelation,
} from "@/graphql";
import { getEnumOptions } from "@/modules/sqdc/helper/SQDCEnumValueHelper";
import LISAInputNumber from "@/components/primevueWrapper/LISAInputNumber.vue";
import { hasConfiguredAccessRight } from "@/functions/accessRightHelper";
import CostCenterSelect from "@/modules/organization/components/selects/CostCenterSelect.vue";
import TechnicalPlaceSelect from "@/modules/organization/components/selects/TechnicalPlaceSelect.vue";
import DatePickerComponent from "@/components/shared/DatePickerComponentNew.vue";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import {
  getSQDCButtonModuleOptions,
  isSQDCButtonModuleType,
} from "@/modules/sqdc/helper/SQDCButtonModuleHelper";

const props = defineProps(FilterUIProps);

type CustomField = (typeof props.types)[number]["fields"][number];
type CustomFilterRow = {
  id: number;
  fieldId?: number;
  value: any;
};

const { t, locale } = useTranslation();
const translateDynamic = (key: string) => String(t(key));
let nextRowId = 1;
const rows = ref<CustomFilterRow[]>([createRow()]);

const siteId = computed(() => props.siteId);

function createRow(filter?: CustomAttributeFilter): CustomFilterRow {
  const field =
    filter?.fieldId == null
      ? undefined
      : allFields.value.find((candidate) => candidate.id === filter.fieldId);
  return {
    id: nextRowId++,
    fieldId: filter?.fieldId,
    value: normalizeFilterValue(field, filter?.value),
  };
}

function isFilterableField(field: CustomField) {
  const supportedScalarTypes = [
    SQDCEventActionTypeFieldType.TEXT,
    SQDCEventActionTypeFieldType.TEXTAREA,
    SQDCEventActionTypeFieldType.NUMBER,
    SQDCEventActionTypeFieldType.BOOLEAN,
    SQDCEventActionTypeFieldType.ENUM,
    SQDCEventActionTypeFieldType.DATE,
    SQDCEventActionTypeFieldType.PERCENT_SHARING,
    SQDCEventActionTypeFieldType.OUTAGE,
    SQDCEventActionTypeFieldType.VETO,
    SQDCEventActionTypeFieldType.RECOMMENDATION,
  ];
  const supportedRelation =
    field.type === SQDCEventActionTypeFieldType.RELATION &&
    [
      SQDCEventTypeFieldRelation.COST_CENTER,
      SQDCEventTypeFieldRelation.TECHNICAL_PLACE,
      SQDCEventTypeFieldRelation.USER,
      SQDCEventTypeFieldRelation.IDEA_CATEGORY,
    ].includes(field.relation);
  return (
    (supportedScalarTypes.includes(field.type) || supportedRelation) &&
    hasConfiguredAccessRight(field.visibleForAdminOnly)
  );
}

const allFields = computed(() =>
  props.types.flatMap((type) => type.fields).filter(isFilterableField),
);

const fieldOptions = computed<LISASelectOption[]>(() =>
  allFields.value.map((field) => ({
    id: field.id,
    label: t(`dynamic.${field.nameId}`),
  })),
);

function selectedField(row: CustomFilterRow) {
  return allFields.value.find((field) => field.id === row.fieldId);
}

function defaultValueForField(field?: CustomField) {
  switch (field?.type) {
    case SQDCEventActionTypeFieldType.BOOLEAN:
      return undefined;
    case SQDCEventActionTypeFieldType.RELATION:
      return field.relation === SQDCEventTypeFieldRelation.USER || field.isArray ? [] : undefined;
    case SQDCEventActionTypeFieldType.ENUM:
      return field.isArray ? [] : undefined;
    case SQDCEventActionTypeFieldType.VETO:
    case SQDCEventActionTypeFieldType.RECOMMENDATION:
      return undefined;
    case SQDCEventActionTypeFieldType.PERCENT_SHARING:
      return [];
    case SQDCEventActionTypeFieldType.OUTAGE:
      return { hasOutage: undefined, dateRange: [] };
    case SQDCEventActionTypeFieldType.DATE:
      return undefined;
    default:
      return undefined;
  }
}

function getButtonModuleSelectOptions(type?: SQDCEventActionTypeFieldType | null) {
  return getSQDCButtonModuleOptions(type).map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));
}

function setDefaultValue(row: CustomFilterRow) {
  row.value = defaultValueForField(selectedField(row));
}

function addRow(filter?: CustomAttributeFilter) {
  rows.value.push(createRow(filter));
}

function removeRow(rowId: number) {
  if (rows.value.length === 1) {
    rows.value = [createRow()];
    return;
  }
  rows.value = rows.value.filter((row) => row.id !== rowId);
}

function toISO(value: unknown) {
  if (DateTime.isDateTime(value)) {
    return value.toISO();
  }
  return value;
}

function normalizeFilterValue(field?: CustomField, value?: unknown) {
  if (field?.type === SQDCEventActionTypeFieldType.DATE && typeof value === "string") {
    return DateTime.fromISO(value);
  }
  if (
    field?.type === SQDCEventActionTypeFieldType.OUTAGE &&
    typeof value === "object" &&
    value != null
  ) {
    const outageValue = value as { hasOutage?: boolean; dateRange?: unknown[] };
    return {
      hasOutage: outageValue.hasOutage,
      dateRange: Array.isArray(outageValue.dateRange)
        ? outageValue.dateRange.map((date) =>
            typeof date === "string" ? DateTime.fromISO(date) : date,
          )
        : [],
    };
  }
  if (
    (field?.type === SQDCEventActionTypeFieldType.PERCENT_SHARING ||
      (field?.type === SQDCEventActionTypeFieldType.RELATION &&
        field.relation === SQDCEventTypeFieldRelation.USER)) &&
    value != null
  ) {
    return Array.isArray(value) ? value : [value];
  }
  return value;
}

function serializeValue(field: CustomField, value: unknown) {
  if (field.type === SQDCEventActionTypeFieldType.DATE) {
    return toISO(value);
  }
  if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
    const outageValue = value as { hasOutage?: boolean; dateRange?: unknown[] };
    return {
      hasOutage: outageValue?.hasOutage ?? false,
      dateRange: Array.isArray(outageValue?.dateRange) ? outageValue.dateRange.map(toISO) : [],
    };
  }
  return value;
}

function hasValue(field: CustomField, value: unknown) {
  if (field.type === SQDCEventActionTypeFieldType.BOOLEAN) {
    return typeof value === "boolean";
  }
  if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
    return typeof (value as { hasOutage?: unknown })?.hasOutage === "boolean";
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value != null && value !== "";
}

function getFilter() {
  return rows.value.flatMap((row) => {
    const field = selectedField(row);
    if (field == null || !hasValue(field, row.value)) {
      return [];
    }

    return [
      new CustomAttributeFilter(
        field.id,
        field.type,
        serializeValue(field, row.value),
        field.relation,
      ),
    ];
  });
}

const completeRowCount = computed(
  () =>
    rows.value.filter((row) => {
      const field = selectedField(row);
      return field != null && hasValue(field, row.value);
    }).length,
);

function applyFilter(filter: CustomAttributeFilter) {
  const firstEmptyRow = rows.value.find((row) => row.fieldId == null);
  if (firstEmptyRow != null) {
    firstEmptyRow.fieldId = filter.fieldId;
    firstEmptyRow.value = normalizeFilterValue(selectedField(firstEmptyRow), filter.value);
    return;
  }
  addRow(filter);
}

defineExpose({
  getFilter,
  applyFilter,
});
</script>

<style lang="scss" scoped>
.custom-filter-list {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.custom-filter-row {
  position: relative;
  display: flex;
  min-width: 0;
  padding-right: 3.5rem;
  gap: 0.5rem;
  align-items: center;
  grid-template-columns: minmax(13rem, 1fr) minmax(13rem, 1fr);
  display: grid;
  margin-bottom: 0.5rem;
}

.custom-field-select,
.custom-field-value {
  width: 100%;
  min-width: 0;
}

.custom-outage-filter {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.custom-outage-filter .custom-field-value {
  flex: 1 1 100%;
}

.custom-filter-icon-button {
  position: absolute;
  top: 0;
  right: 0;
  width: 3rem;
  min-width: 3rem;
}

.custom-filter-add-button {
  min-height: 3.5rem;
  align-self: flex-start;
}

.custom-filter-count {
  min-width: 1.25rem;
  padding: 0.05rem 0.35rem;
  border-radius: var(--p-border-radius-xl);
  background: var(--filter-accent-soft);
  color: var(--filter-accent);
  text-align: center;
}

:global(.custom-filter-popover) {
  width: min(48rem, calc(100vw - 2rem));
}
</style>
